/**
 * Collapsible Meta/TikTok product set card, laid out like the ADSP CCG card
 * (AdspProductSetCard): chevron + entity icon + name/id header with the status badge on
 * the right. Expanding it lazy-loads the per-status product counts and the
 * server-paginated products (ASSET rows joined by
 * parentPublisherEntityId === PRODUCT_SET.publisherEntityId), narrowed by the shared
 * catalog status filter, and lists them one product per row (each row reuses the asset
 * card's media preview). The first card opens by default.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import type { Publisher, PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { PublisherEntityBadge, derivePublisherEntityStatus } from 'src/utils/publisherEntityStatus';
import { REMOVED_ASSET_OPACITY } from './AdspCreativeBlock';
import { AdspPaginationFooter } from './AdspPaginationFooter';
import { CatalogProductRow } from './CatalogProductRow';
import { resolveEmptyStateKey } from './adspEntityHelpers';
import { readProductSetInfo, withPublishedAvailability } from './catalogProductHelpers';
import { EntityIcon } from './EntityIcon';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';
import type { AdsStatusFilter } from './statusFilterParams';
import { useAdspEntityLevel } from './useAdspEntityLevel';
import { CATALOG_COUNT_BUCKETS, useCatalogProductCounts } from './useCatalogProductCounts';

/**
 * Same labels as the shared StatusFilterSelect, so counts and filter read identically. The
 * counts are server-side ledger filters (syncStatus / entityStatus), like the filter itself,
 * so a sports product outside its window counts as Live while its row badge (published
 * availability) reads Removed; the counts row's tooltip says so.
 */
const COUNT_LABELS: Record<AdsStatusFilter, string> = {
  ALL: 'ads_manager_catalog_count_total',
  FAILED: 'ads_manager_filter_failed',
  LIVE: 'ads_manager_filter_live',
  PENDING: 'ads_manager_filter_pending',
  PROCESSING: 'ads_manager_filter_processing',
  REMOVED: 'ads_manager_filter_removed',
};

interface CatalogProductSetCardProps {
  campaignId: string;
  defaultExpanded: boolean;
  productSet: PublisherEntityDto;
  publisher: Publisher;
  statusFilter: AdsStatusFilter;
}

/** Dims a product exactly when its row badge reads Removed (published availability, not ledger status). */
function isRemoved (entity: PublisherEntityDto): boolean {
  return derivePublisherEntityStatus(withPublishedAvailability(entity)) === PublisherEntityBadge.REMOVED;
}

export const CatalogProductSetCard: React.FC<CatalogProductSetCardProps> = ({
  campaignId,
  defaultExpanded,
  productSet,
  publisher,
  statusFilter,
}) => {
  const { t } = useTranslation();
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const info = readProductSetInfo(productSet);
  const productSetId = productSet.publisherEntityId;
  const name = info.name ?? productSetId ?? productSet.sourceEntityId ?? EMPTY_VALUE;
  const testId = `catalog-product-set-${productSet._id}`;

  // Like the product list, counts load only once the card has been expanded (6 queries per card).
  const counts = useCatalogProductCounts({ campaignId, productSetId, publisher, skip: !hasExpanded });
  const { page, query, setPage } = useAdspEntityLevel({
    campaignId,
    parentPublisherEntityId: productSetId,
    publisher,
    publisherEntityType: 'ASSET',
    skip: !hasExpanded || productSetId == null,
    statusFilter,
  });

  const products = (query.data?.data ?? []) as PublisherEntityDto[];
  const pagination = query.data?.meta.pagination;
  const emptyStateKey = resolveEmptyStateKey(statusFilter, 'ads_manager_catalog_no_products');

  const handleToggle = (): void => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(prev => !prev);
  };

  let body: React.ReactNode = null;
  if (isExpanded) {
    if (query.isError) {
      body = <Alert data-testid={`${testId}-error`} size="small" type="error">{t('ads_manager_query_error')}</Alert>;
    } else if (query.isLoading) {
      body = <div data-testid={`${testId}-loading`}><MeridianLoader size="small" type="linear" /></div>;
    } else if (products.length === 0) {
      body = <Text color="secondary" data-testid={`${testId}-empty`}>{t(emptyStateKey)}</Text>;
    } else {
      body = (
        <Column data-testid={`${testId}-products`} spacing="200">
          <Text color="secondary" type="b100">
            {t('ads_manager_catalog_products_label')} ({pagination?.total ?? products.length})
          </Text>
          {products.map(product => (
            <div
              data-testid={`${testId}-product-${product._id}`}
              key={product._id}
              style={isRemoved(product) ? { opacity: REMOVED_ASSET_OPACITY } : undefined}
            >
              <CatalogProductRow entity={product} />
            </div>
          ))}
        </Column>
      );
    }
  }

  return (
    <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
      <Box backgroundColor="subdued" data-testid={testId} spacingInset="400">
        <Column spacing="400">
          <div
            data-testid={`${testId}-toggle`}
            style={{ alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
            onClick={handleToggle}
          >
            <Row alignmentVertical="center" spacing="300">
              <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
              <EntityIcon size={32} type={productSet.publisherEntityType} />
              <Column spacing="none">
                <Text type="b300">{name}</Text>
                <Text color="secondary" type="b100">
                  {t('ads_manager_catalog_product_set_id')}: {productSetId ?? EMPTY_VALUE}
                  {' · '}{t('ads_manager_catalog_catalog_id')}: {info.catalogId ?? EMPTY_VALUE}
                  {info.businessCenterId != null && <>{' · '}{t('ads_manager_catalog_business_center_id')}: {info.businessCenterId}</>}
                </Text>
              </Column>
              {counts.ALL != null && counts.ALL > 0 && (
                <Text color="secondary" type="b100">
                  {t('ads_manager_catalog_products_label')} ({counts.ALL})
                </Text>
              )}
            </Row>
            <Row spacing="200">
              {info.adoptedFromFry && <Tag type="neutral">{t('ads_manager_catalog_adopted_from_fry')}</Tag>}
              <StatusBadge entity={productSet} />
            </Row>
          </div>

          {isExpanded && (
            <div data-testid={`${testId}-counts`} title={t('ads_manager_catalog_counts_ledger_hint')}>
              <Row spacing="400" wrap="down">
                {CATALOG_COUNT_BUCKETS.map(bucket => (
                  <Text color="secondary" data-testid={`${testId}-count-${bucket}`} key={bucket} type="b100">
                    {t(COUNT_LABELS[bucket])}: {counts[bucket] ?? EMPTY_VALUE}
                  </Text>
                ))}
                {info.filterProductCount != null && (
                  <Text color="secondary" data-testid={`${testId}-filter-count`} type="b100">
                    {t('ads_manager_catalog_filter_product_count')}: {info.filterProductCount}
                  </Text>
                )}
              </Row>
            </div>
          )}

          {productSet.lastError != null && (
            <Text color="error" type="b200">{productSet.lastError}</Text>
          )}

          {body}
          {isExpanded && pagination != null && (
            <AdspPaginationFooter
              isFetching={query.isFetching}
              page={page}
              pagination={pagination}
              testId={`${testId}-products`}
              onPageChange={setPage}
            />
          )}
        </Column>
      </Box>
    </div>
  );
};
